import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { AuthHeading, BackButton, Button, Input, Spinner } from 'view/components';
import {
  AuthFormsFooter,
  Agreement,
  Separator,
  GoogleLoginButton,
} from 'view/pages/auth/components';
import {
  AuthHeadingImageTypeEnums,
  commonEmailValidationRules,
  parseError,
} from 'view/pages/auth/helpers';
import * as CS from 'view/pages/auth/commonStyled';
import { useRegistration } from 'view/pages/auth/hooks/useRegistration';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';

interface IFormInput {
  email: string;
}

const RegistrationFormFields: FC<{
  error: { message: string };
  isLoading: boolean;
  status: number;
}> = ({ error, isLoading, status }) => {
  const navigate = useNavigate();

  const {
    clearErrors,
    setError,
    formState: { errors },
  } = useFormContext<IFormInput>();

  useEffect(() => {
    if (!error && status === 204) {
      clearErrors('email');
      navigate('/auth/registration/verify-email', { replace: true });
    } else if (error)
      setError(
        'email',
        { type: 'custom', message: error.message || 'Unknown error' },
        { shouldFocus: true }
      );
  }, [error, status, setError]);

  return (
    <>
      <Input
        name="email"
        label="Email"
        type="email"
        rules={commonEmailValidationRules}
        parseError={parseError}
      />
      <CS.FormWrapperMain component="div">
        <Button
          variant="contained"
          type="submit"
          text="Sign Up"
          disabled={!!errors?.email || isLoading}
          endIcon={
            isLoading ? (
              <Spinner disabled={!!errors?.email || isLoading} variant="contained" />
            ) : null
          }
        />
        <Separator />
        <GoogleLoginButton variant="outlined" />
        <Agreement />
      </CS.FormWrapperMain>
    </>
  );
};

const RegistrationPage = () => {
  const { isLoading, send, error, status } = useRegistration();

  const onSubmit = (formData: IFormInput) => {
    send({ email: formData.email });
  };

  return (
    <CS.Wrapper>
      <CS.BackButtonBox>
        <BackButton text="Back to Authorization" fallbackUrl="/auth/onboarding" />
      </CS.BackButtonBox>
      <CS.AuthMainBox component="section">
        <AuthHeading
          title="Create Account"
          subTitle={'Create your account to unleash \n the full potential of Injobnito'}
          imageType={AuthHeadingImageTypeEnums.CIRCLE}
        />
        <CS.FormWrapper component="section">
          <FormContainer defaultValues={{ email: '' }} onSuccess={onSubmit}>
            <RegistrationFormFields error={error} isLoading={isLoading} status={status} />
          </FormContainer>
          <AuthFormsFooter
            title="Already have an account?"
            hrefTo="/auth/login"
            hrefText="Log In"
          />
        </CS.FormWrapper>
      </CS.AuthMainBox>
    </CS.Wrapper>
  );
};

export default RegistrationPage;
